import { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import OrangeButton from "../../../../components/buttons/OrangeButton";
import FormDescriptionInput from "../../../../components/form_fields/FormDescriptionInput";
import ModalComponent from "../../../../components/modals/ModalFormComponent";
import { IAddEditTableForm } from "../../../../helpers/appHelpers";
import { useManageTableWithSearchParamsData } from "../../../../helpers/useManageTable";
import {
	useCreateOvertimeGroup,
	useUpdateOvertimeGroup,
} from "../../../../services/overtime-group.servicet";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import Label from "../../../../components/form_fields/Label";
import FormCodeInput from "../../../../components/form_fields/FormCodeInput";

interface IFormInputs {
	name: string;
	code: string;
	description: string;
	minHoursRequired: number;
	maxHoursPermitted: number;
	taxable: string;
	active: string;
}

type OvertimeGroupModalFormProps = IAddEditTableForm & {
	reloadTableData?: () => void;
};

const OvertimeGroupModalForm = ({
	open,
	setOpen,
	title,
	isEditForm,
	tableAPIRef,
	reloadTableData,
}: OvertimeGroupModalFormProps) => {
	const { currentCompany } = useCompanyStore();
	const { formDataValues } = useEditFormStore();
	const [uploading, setUploading] = useState(false);

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<IFormInputs>({
		defaultValues: {
			name: isEditForm ? formDataValues?.name : "",
			code: isEditForm ? formDataValues?.code : "",
			description: isEditForm ? formDataValues?.description : "",
			minHoursRequired: isEditForm ? formDataValues?.minHoursRequired : "",
			maxHoursPermitted: isEditForm ? formDataValues?.maxHoursPermitted : "",
			taxable: isEditForm ? (formDataValues?.taxable ? "yes" : "no") : "",
			active: isEditForm ? (formDataValues?.active ? "yes" : "no") : "",
		},
	});

	const { mutate: createOvertimeGroup } = useCreateOvertimeGroup();
	const { mutate: updateOvertimeGroup } = useUpdateOvertimeGroup();
	const { addNewTableData } = useManageTableWithSearchParamsData();

	const onSubmit: SubmitHandler<IFormInputs> = (data: any) => {
		setUploading(true);

		data.taxable = data.taxable === "yes" ? true : false;
		data.active = data.active === "yes" ? true : false;

		if (isEditForm) {
			updateOvertimeGroup(
				{
					payload: data,
					companyId: currentCompany?.id as number,
					id: formDataValues?.id,
				},
				{
					onSuccess: (updatedData) => {
						try {
							tableAPIRef?.current.updateRows([updatedData.data]);
						} catch (error) {
							console.error("Error updating table rows:", error);
							reloadTableData?.();
						}
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		} else {
			createOvertimeGroup(
				{ companyId: currentCompany?.id as number, payload: data },
				{
					onSuccess: () => {
						addNewTableData();
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		}
	};

	const handleClose = () => {
		reset();
		setOpen(false);
	};

	return (
		<ModalComponent
			width={775}
			open={open}
			title={title}
			handleClose={handleClose}
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-6">
					<div className="w-full">
						<Label title="Name" for="name" />
						<input
							autoFocus
							placeholder={"Enter name"}
							type="text"
							{...register("name", {
								required: "Enter name here",
							})}
							id="name"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
								errors.name
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{errors.name && (
							<p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Unique Code" for="code" />
						<FormCodeInput
							register={register}
							attributes={{
								className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
									errors.code
										? " border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`,
							}}
						/>
						{errors.code && (
							<p className="text-red-500 text-sm mt-1">{errors.code.message}</p>
						)}
					</div>
					<div className="col-span-2 w-full">
						<Label title="Description" for="description" />
						<FormDescriptionInput
							register={register}
							attributes={{
								defaultValue: isEditForm ? formDataValues?.description : "",
								className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
									errors.description
										? " border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`,
							}}
						/>
						{errors.description && (
							<p className="text-red-500 text-sm mt-1">
								{errors.description.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Min Hours Required" for="minHoursRequired" />
						<input
							placeholder="Enter minimum hours"
							type="number"
							step="0.01"
							{...register("minHoursRequired", {
								required: "Enter minimum hours required",
								valueAsNumber: true,
							})}
							id="minHoursRequired"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
								errors.minHoursRequired
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{errors.minHoursRequired && (
							<p className="text-red-500 text-sm mt-1">
								{errors.minHoursRequired.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Max Hours Permitted" for="maxHoursPermitted" />
						<input
							placeholder="Enter maximum hours"
							type="number"
							step="0.01"
							{...register("maxHoursPermitted", {
								required: "Enter maximum hours permitted",
								valueAsNumber: true,
							})}
							id="maxHoursPermitted"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
								errors.maxHoursPermitted
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{errors.maxHoursPermitted && (
							<p className="text-red-500 text-sm mt-1">
								{errors.maxHoursPermitted.message}
							</p>
						)}
					</div>
					<div className="col-span-2">
						<div className="flex space-x-12 text-base">
							<h3>Taxable</h3>
							<div className="flex space-x-3 items-center">
								<input
									{...register("taxable", {
										required: "Select taxable option",
									})}
									defaultChecked={
										isEditForm && formDataValues?.taxable === true
									}
									type="radio"
									value={"yes"}
									id="taxable"
								/>
								<label htmlFor="taxable">Yes</label>
							</div>
							<div className="flex space-x-3 items-center">
								<input
									{...register("taxable", {
										required: "Select taxable option",
									})}
									defaultChecked={
										isEditForm && formDataValues?.taxable === false
									}
									type="radio"
									value={"no"}
									id="non-taxable"
								/>
								<label htmlFor="non-taxable">No</label>
							</div>
						</div>
						{errors.taxable && (
							<p className="text-red-500 text-sm mt-1">
								{errors.taxable.message}
							</p>
						)}
					</div>
					<div className="col-span-2">
						<div className="flex space-x-12 text-base">
							<h3>Active</h3>
							<div className="flex space-x-3 items-center">
								<input
									{...register("active", {
										required: "Select active status",
									})}
									defaultChecked={isEditForm && formDataValues?.active === true}
									type="radio"
									value={"yes"}
									id="active"
								/>
								<label htmlFor="active">Yes</label>
							</div>
							<div className="flex space-x-3 items-center">
								<input
									{...register("active", {
										required: "Select active status",
									})}
									defaultChecked={
										isEditForm && formDataValues?.active === false
									}
									type="radio"
									value={"no"}
									id="inactive"
								/>
								<label htmlFor="inactive">No</label>
							</div>
						</div>
						{errors.active && (
							<p className="text-red-500 text-sm mt-1">
								{errors.active.message}
							</p>
						)}
					</div>
				</div>
				<div className="w-full flex justify-end mt-12">
					<OrangeButton
						type="submit"
						title={
							uploading
								? "Uploading..."
								: isEditForm
									? "Update Overtime Group"
									: "Add Overtime Group"
						}
						className="bg-primary-mango text-white h-14 px-8 rounded-lg text-sm"
						disabled={uploading}
					/>
				</div>
			</form>
		</ModalComponent>
	);
};

export default OvertimeGroupModalForm;
